import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Users, Search, AlertTriangle } from 'lucide-react';
import { publicEvenementielApi } from '../../lib/api';

interface GuestOption {
    id: string;
    first_name: string;
    last_name: string;
    email_masked: string;
    already_seated_elsewhere: boolean;
    already_seated_here: boolean;
}

interface TableInfo {
    event_label: string;
    table_number: string;
    guests: GuestOption[];
}

const PoweredByFooter: React.FC = () => (
    <a href="https://gestion.l-iamani.com" target="_blank" rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-1 text-[11px] text-content-muted hover:text-content transition-colors">
        Propulsé par <span className="font-bold">L'IAmani</span>
    </a>
);

/** Page publique scannée via le QR posé sur une table (Jour J, Phase 5 "Menus & Convives") — le convive
 *  choisit son nom dans une liste (jamais de saisie libre, pour éviter doublons/fautes de frappe) pour se
 *  déclarer assis à cette table. Alimente le tableau de bord "suivi en direct" côté établissement. Demande
 *  explicite d'Emeni (2026-08-22). */
export const TableCheckinPage: React.FC = () => {
    const { publicToken, tableNumber } = useParams<{ publicToken: string; tableNumber: string }>();
    const [phase, setPhase] = React.useState<'loading' | 'notfound' | 'ready'>('loading');
    const [info, setInfo] = React.useState<TableInfo | null>(null);
    const [search, setSearch] = React.useState('');
    const [submittingId, setSubmittingId] = React.useState<string | null>(null);
    const [confirmedName, setConfirmedName] = React.useState<string | null>(null);
    const [recap, setRecap] = React.useState<{ category: string; name: string }[] | null>(null);
    const [notResponded, setNotResponded] = React.useState<string | null>(null);
    const [error, setError] = React.useState('');

    const load = React.useCallback(() => {
        if (!publicToken || !tableNumber) return;
        publicEvenementielApi.getTableCheckinInfo(publicToken, tableNumber).then((data: any) => {
            if (data?.error) { setPhase('notfound'); return; }
            setInfo(data);
            setPhase('ready');
        }).catch(() => setPhase('notfound'));
    }, [publicToken, tableNumber]);

    React.useEffect(() => { load(); }, [load]);

    const handleSelect = async (guest: GuestOption) => {
        if (!publicToken || !tableNumber) return;
        setSubmittingId(guest.id);
        setError('');
        try {
            const res: any = await publicEvenementielApi.submitTableCheckin(publicToken, tableNumber, guest.id);
            if (res?.error) { setError(res.error); return; }
            if (res?.not_responded) { setNotResponded(res.message || ''); return; }
            setRecap(Array.isArray(res?.recap) ? res.recap : []);
            setConfirmedName(`${guest.first_name} ${guest.last_name}`.trim());
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la déclaration.');
        } finally {
            setSubmittingId(null);
        }
    };

    if (phase === 'loading') return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-content-muted" size={28} /></div>;
    if (phase === 'notfound' || !info) return (
        <div className="min-h-screen flex items-center justify-center p-6 text-center">
            <p className="text-content-muted">Ce lien n'est plus valide.</p>
        </div>
    );

    if (notResponded) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center gap-3">
            <AlertTriangle size={40} className="text-amber-500" />
            <h1 className="text-lg font-bold text-content">Réponse non reçue</h1>
            <p className="text-sm text-content-muted max-w-xs">{notResponded}</p>
            <PoweredByFooter />
        </div>
    );

    if (confirmedName) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center gap-3">
            <CheckCircle2 size={40} className="text-brand-500" />
            <h1 className="text-lg font-bold text-content">Bienvenue {confirmedName} !</h1>
            <p className="text-sm text-content-muted max-w-xs">C'est confirmé, vous êtes installé(e) à la table {info.table_number}.</p>
            {recap && recap.length > 0 && (
                <div className="w-full max-w-xs bg-surface-soft border border-border rounded-xl p-3 text-left space-y-1.5">
                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest text-center">Votre choix</p>
                    {recap.map((r) => (
                        <p key={r.category} className="text-xs text-content"><span className="text-content-muted">{r.category} : </span><span className="font-bold">{r.name}</span></p>
                    ))}
                </div>
            )}
            <p className="text-[11px] text-content-muted max-w-xs">Si vous changez de table, il faudra scanner à nouveau le QR de votre nouvelle table pour mettre à jour votre déclaration.</p>
            <PoweredByFooter />
        </div>
    );

    const filteredGuests = info.guests.filter((g) => `${g.first_name} ${g.last_name}`.toLowerCase().includes(search.trim().toLowerCase()));

    return (
        <div className="min-h-screen bg-surface px-4 py-8 flex flex-col items-center">
            <div className="w-full max-w-md space-y-5">
                <div className="text-center space-y-1">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-surface-soft border border-border flex items-center justify-center mb-2">
                        <Users size={26} className="text-content-muted" />
                    </div>
                    <h1 className="text-xl font-bold text-content">{info.event_label || 'Privatisation'}</h1>
                    <p className="text-sm text-content-muted">Table {info.table_number} — qui êtes-vous ?</p>
                </div>

                {error && <p className="text-xs text-red-500 font-semibold text-center">{error}</p>}

                <div className="relative">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher votre nom..."
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-surface-soft border border-border text-sm text-content outline-none focus:border-brand-500" />
                </div>

                <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                    {filteredGuests.length === 0 ? (
                        <p className="text-xs text-content-muted text-center py-4">Aucun convive ne correspond.</p>
                    ) : filteredGuests.map((g) => (
                        <button key={g.id} onClick={() => handleSelect(g)} disabled={submittingId === g.id}
                            className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-bold transition-all disabled:opacity-50 ${g.already_seated_here ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface-soft border-border text-content hover:border-brand-500/40'}`}>
                            <div className="flex items-center justify-between gap-2">
                                <span>{g.first_name} {g.last_name}{g.email_masked && <span className="font-normal opacity-60 ml-1.5">({g.email_masked})</span>}</span>
                                {submittingId === g.id && <Loader2 size={14} className="animate-spin shrink-0" />}
                            </div>
                            {g.already_seated_elsewhere && !g.already_seated_here && (
                                <span className="block text-[10px] font-normal opacity-70 mt-0.5">Déjà déclaré(e) à une autre table — cliquer changera votre table</span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="flex justify-center"><PoweredByFooter /></div>
            </div>
        </div>
    );
};
